import { ReactNode } from "react"

import { getBonusModeColor,getBonusTypeColor, getDisplayName } from "features/bonus2/helpers/bonus2.helpers"

import { TableDataType } from "constants/eums"
import { TableColumnsInterface } from "constants/interfaces"

import { AdminBonus2 } from "services/bonus/type"

type CustomRenderFn = (value: any) => ReactNode

const renderBonusName: CustomRenderFn = (value: string | Record<string, string>) => {
  const name = getDisplayName(value)
  return <div className="font-semibold text-white">{name}</div>
}

const renderBonusType: CustomRenderFn = (value: AdminBonus2["type"]) => {
  return <span className={`px-2 py-1 text-xs rounded text-white ${getBonusTypeColor(value)}`}>{value}</span>
}

const renderBonusMode: CustomRenderFn = (value: AdminBonus2["mode"]) => {
  return <span className={`px-2 py-1 text-xs rounded text-white ${getBonusModeColor(value)}`}>{value}</span>
}

const renderStackingMode: CustomRenderFn = (value: AdminBonus2["stacking_mode"]) => {
  const labels: Record<string, string> = {
    NO_STACKING: "None",
    STACK_OLDEST_ACTIVE: "FIFO",
    STACK_ALL_ACTIVE: "All",
    STACK_PROPORTIONAL: "Proportional"
  }
  return <span className="text-xs text-gray-300">{labels[value] || value}</span>
}

const renderStatus: CustomRenderFn = (value: boolean) => {
  return (
    <span className={`px-2 py-1 text-xs rounded text-white ${value ? "bg-emerald-600" : "bg-gray-600"}`}>
      {value ? "Active" : "Inactive"}
    </span>
  )
}

const renderExpiration: CustomRenderFn = (value: number) => {
  return <span className="text-white">{value} days</span>
}

const renderWagering: CustomRenderFn = (value: number) => {
  return <span className="text-white">{value}x</span>
}

export const bonus2TableColumns: TableColumnsInterface[] = [
  {
    value: "name",
    title: "bonus2.columns.name",
    format: TableDataType.STRING,
    sorting: false,
    customRender: renderBonusName as any
  },
  {
    value: "type",
    title: "bonus2.columns.type",
    format: TableDataType.STRING,
    sorting: true,
    customRender: renderBonusType as any
  },
  {
    value: "mode",
    title: "bonus2.columns.mode",
    format: TableDataType.STRING,
    sorting: true,
    customRender: renderBonusMode as any
  },
  {
    value: "amount",
    title: "bonus2.columns.amount",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "wagering_multiplier",
    title: "bonus2.columns.wagering",
    format: TableDataType.STRING,
    sorting: true,
    customRender: renderWagering as any
  },
  {
    value: "expiration_days",
    title: "bonus2.columns.expiration",
    format: TableDataType.STRING,
    sorting: true,
    customRender: renderExpiration as any
  },
  {
    value: "starts_at_iso",
    title: "bonus2.columns.startDate",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "ends_at_iso",
    title: "bonus2.columns.endDate",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "priority",
    title: "bonus2.columns.priority",
    format: TableDataType.NUMBER,
    sorting: true
  },
  {
    value: "stacking_mode",
    title: "bonus2.columns.stacking",
    format: TableDataType.STRING,
    sorting: true,
    customRender: renderStackingMode as any
  },
  {
    value: "active",
    title: "bonus2.columns.status",
    format: TableDataType.STRING,
    sorting: true,
    customRender: renderStatus as any
  }
  // NOTE: the "Created" column is intentionally omitted — bonus-service's
  // BonusTemplate contract carries no created_at/updated_at timestamps, so the
  // thin proxy can't populate them (they'd render as "Invalid Date").
]

/**
 * SOFT-644 — the Archive tab's columns: the main list's columns plus when the template was
 * archived. "Status" is KEPT — archiving deliberately does not touch Active/Inactive (so a
 * player's queued or running bonus is never disturbed), which makes the flag real information
 * here rather than a constant, and tells the operator what the template reverts to on restore.
 */
export const archivedBonus2TableColumns: TableColumnsInterface[] = [
  ...bonus2TableColumns,
  {
    value: "archived_at",
    title: "bonus2.columns.archived",
    format: TableDataType.DATE,
    sorting: false
  }
]
